export interface ObjectLibraryItem {
  /** The Settings row this piece comes from; absent for the built-in library. */
  configId?: string;
  objectType: string;
  name: string;
  category: string;
  widthFt: number;
  heightFt: number;
  seats?: number;
  color: string;
  icon?: string;
  config?: Record<string, unknown>;
  shape?: string;
  strokeColor?: string;
  canHaveChairs?: boolean;
  chairSizeFt?: number;
  isTable?: boolean;
  isBackground?: boolean;
}

// ─── Unified Canvas Item ────────────────────────────────────────

export interface CanvasItem {
  id?: string;
  /**
   * Stable client-side identity, generated when the item is created and
   * persisted inside `config.uid` so it survives a save/reload. Everything
   * that links items to each other (chair -> parent, group membership)
   * keys off this — never off `tableNumber` or `sortOrder`, which are
   * user-editable, reusable numbers, and using them as identity made
   * unrelated items behave as one.
   */
  uid: string;
  objectType: string; // 'TABLE_ROUND', 'TABLE_RECT', 'TABLE_HEAD', 'BAR', 'CHUPPAH', etc.
  label: string;
  x: number;
  y: number;
  widthFt: number;
  heightFt: number;
  rotation: number;
  color: string;
  opacity: number;
  seats: number;
  config: Record<string, unknown>;
  sortOrder: number;
  tableNumber?: number; // Only for seating-enabled items (guest assignment)
  groupId?: string; // Items with the same groupId move together
}

// ─── Table type mapping ─────────────────────────────────────────

export const TABLE_TYPE_MAP: Record<string, string> = {
  ROUND: 'TABLE_ROUND',
  OVAL: 'TABLE_OVAL',
  SQUARE: 'TABLE_SQUARE',
  RECTANGULAR: 'TABLE_RECT',
  HEAD: 'TABLE_HEAD',
  SERPENTINE: 'TABLE_SERPENTINE',
  HALF_ROUND: 'TABLE_HALF_ROUND',
};

export const TABLE_TYPE_REVERSE: Record<string, string> = {
  TABLE_ROUND: 'ROUND',
  TABLE_OVAL: 'OVAL',
  TABLE_SQUARE: 'SQUARE',
  TABLE_RECT: 'RECTANGULAR',
  TABLE_HEAD: 'HEAD',
  TABLE_SERPENTINE: 'SERPENTINE',
  TABLE_HALF_ROUND: 'HALF_ROUND',
};

// Visual shape rendered for each built-in table type (used to sync
// config.shape when the table type changes in the properties panel).
export const TABLE_TYPE_VISUAL_SHAPES: Record<string, string> = {
  TABLE_ROUND: 'CIRCLE',
  TABLE_OVAL: 'OVAL',
  TABLE_SQUARE: 'RECTANGLE',
  TABLE_RECT: 'RECTANGLE',
  TABLE_HEAD: 'RECTANGLE',
  TABLE_SERPENTINE: 'SERPENTINE',
  TABLE_HALF_ROUND: 'HALF_ROUND',
};

export const TABLE_OBJECT_TYPES = new Set(Object.values(TABLE_TYPE_MAP));

export function isTableItem(item: CanvasItem): boolean {
  return item.tableNumber != null;
}

// ─── Defaults ───────────────────────────────────────────────────

export const TABLE_DEFAULTS: Record<string, { widthFt: number; heightFt: number; seats: number }> =
  {
    TABLE_ROUND: { widthFt: 6, heightFt: 6, seats: 10 },
    TABLE_OVAL: { widthFt: 10, heightFt: 6, seats: 10 },
    TABLE_SQUARE: { widthFt: 5, heightFt: 5, seats: 8 },
    TABLE_RECT: { widthFt: 10, heightFt: 4, seats: 12 },
    TABLE_HEAD: { widthFt: 15, heightFt: 3, seats: 14 },
    TABLE_SERPENTINE: { widthFt: 6, heightFt: 6, seats: 5 },
    TABLE_HALF_ROUND: { widthFt: 6, heightFt: 3, seats: 5 },
  };

export const TABLE_SHAPE_LABELS: Record<string, string> = {
  TABLE_ROUND: 'Round',
  TABLE_OVAL: 'Oval',
  TABLE_SQUARE: 'Square',
  TABLE_RECT: 'Rectangular',
  TABLE_HEAD: 'Head Table',
  TABLE_SERPENTINE: 'Serpentine',
  TABLE_HALF_ROUND: 'Half-round',
};

// Default colors for tables (when no explicit color set)
export const TABLE_DEFAULT_COLOR = '#e0e0e0';
export const HEAD_TABLE_COLOR = '#c4b5fd';

// ─── Legacy types (kept for backward compat during migration) ───

/** @deprecated Use CanvasItem instead */
export interface CanvasTable {
  id?: string;
  tableNumber: number;
  label: string;
  type: 'ROUND' | 'OVAL' | 'SQUARE' | 'RECTANGULAR' | 'HEAD';
  x: number;
  y: number;
  rotation: number;
  width: number;
  height: number;
  seats: number;
}

/** @deprecated Use CanvasItem instead */
export interface CanvasObject {
  id?: string;
  objectType: string;
  label: string;
  x: number;
  y: number;
  widthFt: number;
  heightFt: number;
  rotation: number;
  color: string;
  opacity: number;
  seats?: number;
  config?: Record<string, unknown>;
  sortOrder: number;
}

/** @deprecated Use TABLE_DEFAULTS instead (now uses feet) */
export const LEGACY_TABLE_DEFAULTS: Record<string, Partial<CanvasTable>> = {
  ROUND: { width: 120, height: 120, seats: 10 },
  OVAL: { width: 200, height: 120, seats: 10 },
  SQUARE: { width: 100, height: 100, seats: 8 },
  RECTANGULAR: { width: 200, height: 80, seats: 12 },
  HEAD: { width: 300, height: 60, seats: 14 },
};
